import { escapeHtml, markdownToHtml } from './signing-markdown.js';

/**
 * Convention document HTML: one renderer for the admin composer preview and
 * the API PDF bake. Callers inject assets and how a `{{variable}}` slot prints.
 */

export type ConventionHtmlBlock = {
  id: string;
  title: string;
  level: 1 | 2;
  bodyMarkdown: string;
  kind?: string;
  fields?: Record<string, string>;
  rowId?: string;
  children?: ConventionHtmlBlock[];
};

export type ConventionHtmlOptions = {
  /** Prefix legends with 1 / 1.1 style numbers. */
  numbered?: boolean;
  /** Optional cover sheet before body. */
  coverPage?: boolean;
  /** Optional table-of-contents sheet before body. */
  tocPage?: boolean;
};

export type ConventionOutlineEntry = {
  id: string;
  title: string;
  level: 1 | 2;
  number: string | null;
};

export type ConventionHtmlAssets = {
  logoDataUri: string;
  flowMeshDataUri: string;
  /** `@font-face` rules for Fraunces, Source Sans 3, Space Grotesk, JetBrains Mono. */
  fontFaceCss: string;
};

/** Returns HTML for one `{{id}}` slot. `variable` is the nested variable block, when found. */
export type ConventionSlotRenderer = (
  id: string,
  variable: ConventionHtmlBlock | undefined,
) => string;

export type ConventionHtmlContext = {
  assets: ConventionHtmlAssets;
  renderSlot: ConventionSlotRenderer;
  /** When set, replaces the empty signature pad (measured sign anchor on the PDF path). */
  renderSignaturePad?: (block: ConventionHtmlBlock) => string | null;
};

export const MAX_BLOCKS_PER_ROW = 3;

export type BlockRowUnit<T extends ConventionHtmlBlock = ConventionHtmlBlock> =
  | { type: 'solo'; block: T }
  | { type: 'row'; rowId: string; blocks: T[] };

const SLOT_SPLIT_RE = /(\{\{[^}]+\}\})/g;
const SLOT_RE = /^\{\{([^}]+)\}\}$/;

function isVariable(block: Pick<ConventionHtmlBlock, 'kind'>): boolean {
  return block.kind === 'variable';
}

const PHONE_SLOT_ICON =
  '<svg class="var-slot-icon" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2"/></svg>';
const WHATSAPP_SLOT_ICON =
  '<svg class="var-slot-icon" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>';

/** Notices label text that gets phone + WhatsApp icons before it (not on the value chip). */
export const PHONE_WHATSAPP_LABEL = 'Phone / WhatsApp:';

/** Prefix icons before the Phone / WhatsApp label in escaped HTML text. */
export function decoratePhoneWhatsAppLabelHtml(escapedText: string): string {
  if (!escapedText.includes(PHONE_WHATSAPP_LABEL)) return escapedText;
  return escapedText.split(PHONE_WHATSAPP_LABEL).join(
    `${PHONE_SLOT_ICON}${WHATSAPP_SLOT_ICON} ${PHONE_WHATSAPP_LABEL}`,
  );
}

/** @deprecated Icons belong on the label; kept for callers that still check titles. */
export function conventionSlotIconHtml(_title: string | null | undefined): string {
  return '';
}

/** NDA meta-row right column — hide from every recipient-facing document surface. */
function isAgreementReferenceLabel(label: string): boolean {
  const n = label.trim().toLowerCase();
  if (!n) return false;
  return (
    n === 'agreement reference' ||
    n === 'reference' ||
    n === 'référence' ||
    n === 'reference convention' ||
    n === 'référence convention' ||
    n.includes('agreement reference')
  );
}

export function isPageBreakBlock(block: Pick<ConventionHtmlBlock, 'kind'>): boolean {
  return block.kind === 'page-break';
}

export function isPartyBlock(block: Pick<ConventionHtmlBlock, 'kind'>): boolean {
  return block.kind === 'party-wapharma' || block.kind === 'party-partner';
}

export function isSignatureStripBlock(block: Pick<ConventionHtmlBlock, 'kind'>): boolean {
  return block.kind === 'signature-strip';
}

export function isNumberedSectionBlock(block: Pick<ConventionHtmlBlock, 'kind' | 'level'>): boolean {
  return (block.kind ?? 'section') === 'section' && block.level === 1;
}

export function isOutlineEligibleBlock(block: Pick<ConventionHtmlBlock, 'kind' | 'level'>): boolean {
  const kind = block.kind ?? 'section';
  if (kind === 'section' || block.level === 2) return true;
  return kind === 'numbered-list' || kind === 'lettered-list' || kind === 'paragraph';
}

/** Group consecutive root blocks that share a rowId (max 3 per row). */
export function groupBlocksIntoRowUnits<T extends ConventionHtmlBlock>(blocks: T[]): BlockRowUnit<T>[] {
  const units: BlockRowUnit<T>[] = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i]!;
    if (isPageBreakBlock(block) || !block.rowId) {
      units.push({ type: 'solo', block: { ...block, rowId: undefined } });
      i += 1;
      continue;
    }
    const rowId = block.rowId;
    const row: T[] = [];
    while (
      i < blocks.length &&
      blocks[i]!.rowId === rowId &&
      !isPageBreakBlock(blocks[i]!) &&
      row.length < MAX_BLOCKS_PER_ROW
    ) {
      row.push(blocks[i]!);
      i += 1;
    }
    if (row.length === 1) units.push({ type: 'solo', block: row[0]! });
    else units.push({ type: 'row', rowId, blocks: row });
  }
  return units;
}

function assignSectionNumbers(blocks: ConventionHtmlBlock[]): Map<string, string> {
  const map = new Map<string, string>();
  let major = 0;
  for (const block of blocks) {
    if (!isNumberedSectionBlock(block)) continue;
    major += 1;
    map.set(block.id, String(major));
    let minor = 0;
    for (const child of block.children ?? []) {
      if (isVariable(child)) continue;
      minor += 1;
      map.set(child.id, `${major}.${minor}`);
    }
  }
  return map;
}

export function buildConventionOutlineEntries(
  blocks: ConventionHtmlBlock[],
  numbered: boolean,
): ConventionOutlineEntry[] {
  const flat: ConventionHtmlBlock[] = [];
  for (const block of blocks) {
    flat.push(block);
    if (block.children?.length) flat.push(...block.children);
  }
  const numbers = numbered ? assignSectionNumbers(blocks) : null;
  return flat
    .filter((block) => isOutlineEligibleBlock(block))
    .map((block) => ({
      id: block.id,
      title: block.title || (block.kind ?? 'section'),
      level: block.level === 2 ? 2 : 1,
      number: numbers?.get(block.id) ?? null,
    }));
}

/* ---------- slots ---------- */

function variableById(children: ConventionHtmlBlock[] | undefined, id: string) {
  return (children ?? []).find((child) => child.id === id && isVariable(child));
}

/** Escape plain text and print each `{{id}}` through the slot renderer. */
export function renderConventionInlineSlots(
  text: string,
  children: ConventionHtmlBlock[] | undefined,
  renderSlot: ConventionSlotRenderer,
): string {
  if (!text.includes('{{')) return decoratePhoneWhatsAppLabelHtml(escapeHtml(text));
  return text
    .split(SLOT_SPLIT_RE)
    .filter((part) => part !== '')
    .map((part) => {
      const match = SLOT_RE.exec(part);
      if (!match) return decoratePhoneWhatsAppLabelHtml(escapeHtml(part));
      const id = match[1]!;
      return renderSlot(id, variableById(children, id));
    })
    .join('');
}

/** Markdown body with each `{{id}}` printed through the slot renderer. */
export function renderConventionMarkdownSlots(
  text: string,
  children: ConventionHtmlBlock[] | undefined,
  renderSlot: ConventionSlotRenderer,
): string {
  if (!text.includes('{{')) return decoratePhoneWhatsAppLabelHtml(markdownToHtml(text));
  const tokens = new Map<string, string>();
  const marked = text.replace(/\{\{([^}]+)\}\}/g, (_full, id: string) => {
    const token = `VARSLOT${id.replace(/[^A-Za-z0-9]/g, '')}${tokens.size}VARSLOT`;
    tokens.set(token, id);
    return token;
  });
  let html = decoratePhoneWhatsAppLabelHtml(markdownToHtml(marked));
  for (const [token, id] of tokens) {
    html = html.split(token).join(renderSlot(id, variableById(children, id)));
  }
  return html;
}

/* ---------- CSS ---------- */

export function conventionDocumentCss(assets: ConventionHtmlAssets): string {
  const mesh = assets.flowMeshDataUri;
  return `
${assets.fontFaceCss}
:root {
  --fg: #243038;
  --muted: #5a6d7a;
  --line: #d1d9df;
  --line-soft: #e8ecef;
  --accent: #1b4332;
  --accent-mid: #2d6a4f;
  --wash: #f6f4ee;
  --card: #faf9f5;
  --header-bg: #faf9f5;
  --main-bg: #ffffff;
  --shell-radius: 28px;
  --shell-lip: color-mix(in srgb, #0f2419 7%, transparent);
  --font-body: 'Source Sans 3', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
  --font-label: 'Space Grotesk', system-ui, -apple-system, 'Segoe UI', sans-serif;
  --font-display: 'Fraunces', Georgia, 'Times New Roman', serif;
  --font-mono: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
* { box-sizing: border-box; }
body {
  font-family: var(--font-body);
  color: var(--fg);
  margin: 0;
  padding: 0;
  background: #e8ecef;
  -webkit-font-smoothing: antialiased;
}
.sheet {
  max-width: 210mm;
  margin: 12px auto;
  background: var(--header-bg);
  box-shadow: 0 1px 3px rgb(0 0 0 / 0.08), 0 8px 24px rgb(0 0 0 / 0.06);
  overflow: hidden;
  break-after: page;
  page-break-after: always;
}
.sheet:last-child {
  break-after: auto;
  page-break-after: auto;
  margin-bottom: 12px;
}
.page {
  min-height: 260mm;
  display: flex;
  flex-direction: column;
  padding: 0;
}
.header {
  position: relative;
  isolation: isolate;
  flex-shrink: 0;
  background: var(--header-bg);
}
.header::after,
.footer::after {
  content: '';
  pointer-events: none;
  position: absolute;
  inset: 0;
  z-index: 0;
  opacity: 0.9;
  mix-blend-mode: multiply;
  background-image: url("${mesh}");
  background-repeat: repeat-x;
  background-size: 960px 100%;
  background-position: center center;
}
.headerInner {
  position: relative;
  z-index: 1;
  padding: 22px 34px 20px;
}
.brand-logo { display: block; height: 28px; width: auto; max-width: 220px; }
.main {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  background: var(--main-bg);
  border-radius: var(--shell-radius);
  border-top: 1px solid var(--shell-lip);
  border-bottom: 1px solid var(--shell-lip);
  padding: 22px 34px 28px;
}
.doc-head { margin: 0 0 28px; }
.doc-kicker {
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--muted);
  margin: 10px 0 0;
}
.doc-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 12px;
}
.doc-title {
  font-family: var(--font-display);
  font-optical-sizing: auto;
  font-weight: 600;
  font-size: 26px;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: var(--fg);
  margin: 0;
  flex: 0 1 auto;
  min-width: 0;
  text-wrap: balance;
}
.doc-title-rule {
  flex: 0 0 56px;
  width: 56px;
  height: 1px;
  margin-top: 0.52em;
  align-self: center;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--muted) 42%, transparent),
    color-mix(in srgb, var(--muted) 4%, transparent)
  );
}
.toc-page .doc-outline { margin: 0; padding: 0; flex: 1 1 auto; }
.doc-outline-title {
  font-family: var(--font-label);
  font-size: 11px;
  font-weight: 300;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
  margin: 0 0 14px;
}
.doc-outline-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.doc-outline-item {
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.4;
  color: var(--fg);
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.doc-outline-item a {
  color: inherit;
  text-decoration: none;
  flex: 1 1 auto;
  min-width: 0;
  border-bottom: 1px dotted color-mix(in srgb, var(--muted) 35%, transparent);
  padding-bottom: 2px;
}
.doc-outline-num {
  font-family: var(--font-mono);
  font-weight: 400;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
  flex: 0 0 auto;
  min-width: 2rem;
}
.doc-outline-item-sub { padding-left: 1.25rem; font-size: 12px; color: var(--muted); }
.cover-main {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 18px;
  min-height: 0;
  flex: 1 1 auto;
  padding: 48px 34px 40px;
}
.cover-parties,
.block-row {
  margin-top: 28px;
  display: grid;
  gap: 14px;
}
.cover-parties { grid-template-columns: 1fr 1fr; }
.block-row { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.doc-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
  margin: 0 0 14px;
  font-family: var(--font-label);
  font-size: 11px;
  font-weight: 300;
  letter-spacing: 0.03em;
  color: var(--muted);
}
.doc-meta-code { font-weight: 500; color: var(--fg); }
.doc-meta-badge {
  padding: 2px 8px;
  border: 1px solid var(--line);
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 9px;
}
.doc-meta-version { color: var(--muted); }
.field-pair { margin-top: 4px; }
.field-row { margin-bottom: 8px; }
.field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 14px; }
.paragraph-block { margin: 0 0 12px; font-size: 12.5px; line-height: 1.55; }
.disclaimer {
  margin: 12px 0;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--wash);
}
.disclaimer-title {
  font-family: var(--font-label);
  font-size: 11px;
  font-weight: 300;
  letter-spacing: 0.02em;
  color: var(--muted);
  margin: 0 0 8px;
}
.disclaimer-body { font-size: 12px; line-height: 1.5; }
.disclaimer-body > :first-child { margin-top: 0; }
.disclaimer-body > :last-child { margin-bottom: 0; }
.sig-pad, .stamp-pad {
  min-height: 56px;
  margin-top: 10px;
  border-bottom: 1px solid color-mix(in srgb, var(--muted) 35%, transparent);
}
.sig-pad .sign-anchor {
  display: block;
  width: 100%;
  min-height: 48px;
  box-sizing: border-box;
}
.stamp-pad {
  min-height: 72px;
  border: 1px dashed var(--line);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  font-size: 11px;
}
.page-break-marker {
  break-before: page;
  page-break-before: always;
  height: 0;
  margin: 0;
  padding: 0;
  border: 0;
}
.var-slot {
  display: inline;
  padding: 0 0.2em;
  border-bottom: 1px solid currentColor;
  background: color-mix(in srgb, var(--accent, #1b4332) 10%, transparent);
  border-radius: 2px;
}
.var-slot[data-empty="1"] { font-style: italic; color: var(--muted); }
.var-slot-icon {
  display: inline-block;
  width: 0.85em;
  height: 0.85em;
  vertical-align: -0.12em;
  margin-right: 0.25em;
}
.var-value { font: inherit; }
.var-value .var-slot-icon { margin-right: 0.25em; }
/* Numbers, dates and references: section numbers, outline, pagination and mono slots. */
.num,
.var-mono {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
}
.var-mono { font-size: 0.92em; }
.sign-anchor {
  display: inline-block;
  min-width: 8em;
  min-height: 1.5em;
  padding: 1px 6px;
  border-bottom: 1px solid color-mix(in srgb, var(--muted) 55%, transparent);
  background: color-mix(in srgb, var(--accent, #1b4332) 5%, transparent);
  border-radius: 2px;
  color: transparent;
  text-decoration: none;
  vertical-align: baseline;
}
.section { margin-top: 18px; }
.main > .section:first-child { margin-top: 0; }
.grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.card { border-radius: 10px; padding: 10px 12px; background: var(--wash); }
.label {
  font-size: 9.5px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: .7px;
}
.value { font-size: 13px; font-weight: 600; margin-top: 3px; }
.compose-body { display: flex; flex-direction: column; gap: 18px; }
.atom-shell { min-width: 0; padding-top: 14px; overflow: visible; }
.atom {
  position: relative;
  margin: 0;
  min-width: 0;
  width: 100%;
  overflow: visible;
  padding: 16px 14px 16px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: transparent;
}
.atom-filled { background: var(--wash); }
.atom-legend {
  position: absolute;
  top: 0;
  left: 12px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  max-width: calc(100% - 24px);
  margin: 0;
  padding: 0 6px;
  line-height: 1.2;
  transform: translateY(-50%);
  background: var(--main-bg);
  overflow: visible;
}
.atom-legend-text {
  font-family: var(--font-label);
  font-size: 11px;
  font-weight: 300;
  letter-spacing: 0.02em;
  color: var(--muted);
}
.atom-body { min-width: 0; font-family: var(--font-body); }
.atom-body > :first-child { margin-top: 0; }
.atom-body > :last-child { margin-bottom: 0; }
.atom-body p { margin: 0 0 10px; font-size: 12.5px; line-height: 1.55; font-family: var(--font-body); }
.atom-body ul, .atom-body ol {
  margin: 0 0 10px;
  padding-left: 1.25rem;
  font-size: 12.5px;
  line-height: 1.55;
  font-family: var(--font-body);
}
.atom-body li { margin-bottom: 4px; }
.atom-body strong { font-weight: 600; }
.subsections { display: flex; flex-direction: column; gap: 16px; margin-top: 16px; padding-left: 18px; }
.subsection { min-width: 0; }
.subsection-title {
  font-family: var(--font-label);
  font-size: 11px;
  font-weight: 300;
  letter-spacing: 0.02em;
  color: var(--muted);
  margin: 0 0 10px;
}
.subsection-body { min-width: 0; }
.subsection-body > :first-child { margin-top: 0; }
.subsection-body > :last-child { margin-bottom: 0; }
.subsection-body p { margin: 0 0 10px; font-size: 12.5px; line-height: 1.55; }
.empty-hint { color: var(--muted); font-size: 12px; font-style: italic; }
.footer {
  position: relative;
  isolation: isolate;
  flex-shrink: 0;
  background: var(--header-bg);
}
.footerInner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 14px 34px 18px;
}
.pagination {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
.pagination b { color: var(--accent); font-weight: 600; }
.print-header,
.print-footer,
.print-card,
.print-head-space,
.print-foot-space { display: none; }
.print-frame,
.print-frame > tbody,
.print-frame > tbody > tr,
.print-frame > tbody > tr > td { display: contents; }
@page { size: A4 portrait; margin: 0; }
@media print {
  :root {
    --print-header-h: 70px;
    --print-footer-h: 48px;
  }
  html, body {
    background: var(--header-bg) !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .sheet { max-width: none; width: 100%; margin: 0; box-shadow: none; overflow: visible; }
  /* Chromium repeats <thead>/<tfoot> on every printed page: the empty rows keep
     content clear of the fixed header and footer drawn on every page. */
  .page { display: block; min-height: 0; }
  .page > .header,
  .page > .footer { display: none; }
  .print-frame { display: table; width: 100%; border-collapse: collapse; }
  .print-frame > tbody { display: table-row-group; }
  .print-frame > tbody > tr { display: table-row; }
  .print-frame td { display: table-cell; padding: 0; }
  .print-head-space { display: table-header-group; }
  .print-foot-space { display: table-footer-group; }
  .print-head-space td { height: var(--print-header-h); }
  .print-foot-space td { height: var(--print-footer-h); }
  .print-header,
  .print-footer,
  .print-card { display: block; position: fixed; left: 0; right: 0; }
  .print-header,
  .print-footer { z-index: 2; }
  .print-header { top: 0; height: var(--print-header-h); }
  .print-footer { bottom: 0; height: var(--print-footer-h); }
  /* The white card is page chrome too: it spans header to footer on every page,
     however little content the page holds. */
  .print-card {
    z-index: 0;
    top: var(--print-header-h);
    bottom: var(--print-footer-h);
    background: var(--main-bg);
    border-radius: var(--shell-radius);
    border-top: 1px solid var(--shell-lip);
    border-bottom: 1px solid var(--shell-lip);
  }
  .print-header > .header,
  .print-footer > .footer { height: 100%; box-sizing: border-box; }
  .main {
    background: transparent;
    border: 0;
    border-radius: 0;
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
  }
  /* Single-page sheets fill the band between header and footer (slack avoids a spill page). */
  .sheet-cover .main,
  .sheet-toc .main {
    box-sizing: border-box;
    min-height: calc(297mm - var(--print-header-h) - var(--print-footer-h) - 4px);
  }
  .atom-shell,
  .block-row,
  .field-row,
  .subsection,
  .doc-head {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  /* Keep a lead-in line ("Signed by…") on the page of the row it introduces. */
  .paragraph-block:has(+ .block-row) { break-after: avoid; page-break-after: avoid; }
  .header::after,
  .footer::after {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
`;
}

/* ---------- blocks ---------- */

type Ctx = ConventionHtmlContext;

function inline(text: string, vars: ConventionHtmlBlock[] | undefined, ctx: Ctx) {
  return renderConventionInlineSlots(text, vars, ctx.renderSlot);
}

function markdown(text: string, vars: ConventionHtmlBlock[] | undefined, ctx: Ctx) {
  return renderConventionMarkdownSlots(text, vars, ctx.renderSlot);
}

function sectionNumberHtml(number: string): string {
  return `<span class="num">${escapeHtml(number)}.</span>`;
}

function sectionAtom(
  label: string,
  bodyHtml: string,
  ctx: Ctx,
  opts?: { filled?: boolean; id?: string; variables?: ConventionHtmlBlock[]; number?: string },
): string {
  const atomClass = opts?.filled ? 'atom atom-filled' : 'atom';
  const idAttr = opts?.id ? ` id="${escapeHtml(opts.id)}"` : '';
  const num = opts?.number ? `${sectionNumberHtml(opts.number)} ` : '';
  return `<div class="atom-shell"${idAttr}>
  <fieldset class="${atomClass}">
    <legend class="atom-legend"><span class="atom-legend-text">${num}${inline(label, opts?.variables, ctx)}</span></legend>
    <div class="atom-body">${bodyHtml}</div>
  </fieldset>
</div>`;
}

function partyBlockHtml(block: ConventionHtmlBlock, ctx: Ctx): string {
  if (block.kind === 'party-wapharma') {
    const value = block.bodyMarkdown.trim() || 'WaPharma';
    return sectionAtom('WaPharma', `<div class="value">${inline(value, block.children, ctx)}</div>`, ctx, {
      filled: true,
      id: block.id,
      variables: block.children,
    });
  }
  const name = block.title.trim() || 'Partenaire';
  const signatory = block.bodyMarkdown.trim();
  const body = `<div class="value">${inline(name, block.children, ctx)}</div>${
    signatory
      ? `<div class="value" style="font-weight:400;margin-top:4px">${inline(signatory, block.children, ctx)}</div>`
      : ''
  }`;
  return sectionAtom('Partner', body, ctx, { id: block.id });
}

function labeledValueHtml(
  label: string,
  value: string,
  vars: ConventionHtmlBlock[] | undefined,
  ctx: Ctx,
): string {
  return `<div class="field-row">
  <div class="label">${escapeHtml(label)}</div>
  <div class="value">${inline(value, vars, ctx) || '—'}</div>
</div>`;
}

function specialBlockHtml(block: ConventionHtmlBlock, ctx: Ctx): string | null {
  const kind = block.kind ?? 'section';
  const kids = block.children;
  switch (kind) {
    case 'section':
    case 'party-wapharma':
    case 'party-partner':
      return null;
    case 'doc-title': {
      const t = block.title.trim() || block.bodyMarkdown.trim();
      if (!t) return `<div class="doc-head" id="${escapeHtml(block.id)}"></div>`;
      return `<header class="doc-head" id="${escapeHtml(block.id)}">
  <div class="doc-title-row">
    <h1 class="doc-title">${inline(t, kids, ctx)}</h1>
    <div class="doc-title-rule" aria-hidden="true"></div>
  </div>
</header>`;
    }
    case 'doc-subtitle': {
      const t = block.bodyMarkdown.trim() || block.title.trim();
      if (!t) return '';
      return `<p class="doc-kicker" id="${escapeHtml(block.id)}">${inline(t, kids, ctx)}</p>`;
    }
    case 'doc-meta': {
      const version = block.fields?.version?.trim();
      return `<div class="doc-meta" id="${escapeHtml(block.id)}">
  <span class="doc-meta-code">${escapeHtml(block.title)}</span>
  <span class="doc-meta-badge">${escapeHtml(block.bodyMarkdown || 'FILLABLE')}</span>
  ${version ? `<span class="doc-meta-version">${escapeHtml(version)}</span>` : ''}
</div>`;
    }
    case 'meta-row':
    case 'field-pair': {
      const left = sectionAtom(
        block.title || 'Left',
        `<div class="value">${inline(block.fields?.leftValue ?? '', kids, ctx)}</div>`,
        ctx,
        { variables: kids },
      );
      const rightLabel = (block.bodyMarkdown ?? '').trim();
      const rightValue = (block.fields?.rightValue ?? '').trim();
      /** Recipients must never see CONV / Agreement Reference (incl. old drafts). */
      const hideAgreementReference =
        kind === 'meta-row' && isAgreementReferenceLabel(rightLabel);
      const right =
        !hideAgreementReference && (rightLabel || rightValue)
          ? sectionAtom(
              rightLabel || 'Right',
              `<div class="value">${inline(block.fields?.rightValue ?? '', kids, ctx)}</div>`,
              ctx,
              { variables: kids },
            )
          : '';
      return `<div class="grid field-pair" id="${escapeHtml(block.id)}">
  ${left}${right ? `\n  ${right}` : ''}
</div>`;
    }
    case 'text-field':
    case 'date-field':
      return sectionAtom(
        block.title || (kind === 'date-field' ? 'Date' : 'Field'),
        `<div class="value">${inline(block.bodyMarkdown, kids, ctx)}</div>`,
        ctx,
        { id: block.id, variables: kids },
      );
    case 'field-grid': {
      const cells = Object.entries(block.fields ?? {})
        .map(([key, val]) =>
          labeledValueHtml(
            key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()),
            val,
            kids,
            ctx,
          ),
        )
        .join('\n');
      return sectionAtom(block.title || 'Party details', `<div class="field-grid">${cells}</div>`, ctx, {
        id: block.id,
      });
    }
    case 'paragraph': {
      const body = block.bodyMarkdown
        ? markdown(block.bodyMarkdown, kids, ctx)
        : `<p class="empty-hint">—</p>`;
      if (block.title.trim()) {
        return sectionAtom(block.title, body, ctx, { id: block.id, variables: kids });
      }
      return `<div class="paragraph-block" id="${escapeHtml(block.id)}">${body}</div>`;
    }
    case 'numbered-list':
    case 'lettered-list': {
      const body = block.bodyMarkdown
        ? markdown(block.bodyMarkdown, kids, ctx)
        : `<p class="empty-hint">—</p>`;
      return sectionAtom(block.title || (kind === 'numbered-list' ? 'List' : 'Exclusions'), body, ctx, {
        id: block.id,
        variables: kids,
      });
    }
    case 'disclaimer':
      return `<aside class="disclaimer" id="${escapeHtml(block.id)}">
  ${block.title.trim() ? `<div class="disclaimer-title">${inline(block.title, kids, ctx)}</div>` : ''}
  <div class="disclaimer-body">${block.bodyMarkdown ? markdown(block.bodyMarkdown, kids, ctx) : '—'}</div>
</aside>`;
    case 'signature-strip': {
      const f = block.fields ?? {};
      const details = [
        f.signatoryName?.trim() ? labeledValueHtml('Name', f.signatoryName, kids, ctx) : '',
        f.capacity?.trim() ? labeledValueHtml('Title', f.capacity, kids, ctx) : '',
        f.date?.trim() ? labeledValueHtml('Date', f.date, kids, ctx) : '',
      ]
        .filter(Boolean)
        .join('\n');
      const signature = f.signature?.trim() ? inline(f.signature, kids, ctx) : '';
      const pad = signature
        ? `<div class="value">${signature}</div>`
        : (ctx.renderSignaturePad?.(block) ?? '<div class="sig-pad" aria-hidden="true"></div>');
      return sectionAtom(
        block.title || 'Signature',
        `${details}${pad}`,
        ctx,
        { id: block.id, variables: kids },
      );
    }
    case 'stamp':
      return sectionAtom(
        block.title || 'Stamp / Seal',
        `<div class="stamp-pad">${escapeHtml(block.bodyMarkdown || '')}</div>`,
        ctx,
        { id: block.id },
      );
    case 'page-break':
      return `<div class="page-break-marker" id="${escapeHtml(block.id)}" data-page-break="1"></div>`;
    case 'variable':
      return ctx.renderSlot(block.id, block);
    default:
      return `<div class="section"><p><b>${inline(block.title, kids, ctx)}</b></p>${
        block.bodyMarkdown ? markdown(block.bodyMarkdown, kids, ctx) : ''
      }</div>`;
  }
}

function subsectionHtml(
  block: ConventionHtmlBlock,
  parentVars: ConventionHtmlBlock[] | undefined,
  numbers: Map<string, string> | null,
  ctx: Ctx,
): string {
  const vars = [...(block.children ?? []), ...(parentVars ?? [])];
  const num = numbers?.get(block.id);
  const title = inline(block.title, vars, ctx);
  const label = num ? `${sectionNumberHtml(num)} ${title}` : title;
  const body = block.bodyMarkdown ? markdown(block.bodyMarkdown, vars, ctx) : `<p class="empty-hint">—</p>`;
  return `<section class="subsection" id="${escapeHtml(block.id)}">
  <h3 class="subsection-title">${label}</h3>
  <div class="subsection-body">${body}</div>
</section>`;
}

function renderOneRootBlockHtml(
  block: ConventionHtmlBlock,
  numbers: Map<string, string> | null,
  ctx: Ctx,
): string {
  if (isPartyBlock(block)) return partyBlockHtml(block, ctx);
  const special = specialBlockHtml(block, ctx);
  if (special !== null) return special;
  const num = numbers?.get(block.id);
  const kids = (block.children ?? []).filter((child) => !isVariable(child));
  const nested =
    kids.length > 0
      ? `<div class="subsections">${kids.map((c) => subsectionHtml(c, block.children, numbers, ctx)).join('\n')}</div>`
      : '';
  const lead = block.bodyMarkdown ? markdown(block.bodyMarkdown, block.children, ctx) : '';
  const body = lead || nested ? `${lead}${nested}` : `<p class="empty-hint">—</p>`;
  return sectionAtom(block.title, body, ctx, {
    id: block.id,
    variables: block.children,
    number: num,
  });
}

export function renderConventionSectionBlocks(
  blocks: ConventionHtmlBlock[],
  opts: ConventionHtmlOptions,
  ctx: Ctx,
): { bodyHtml: string; outline: ConventionOutlineEntry[] } {
  const numbered = Boolean(opts.numbered);
  const numbers = numbered ? assignSectionNumbers(blocks) : null;
  const outline = buildConventionOutlineEntries(blocks, numbered);
  const chunks: string[] = [];
  for (const unit of groupBlocksIntoRowUnits(blocks)) {
    if (unit.type === 'row') {
      const cells = unit.blocks.map((b) => renderOneRootBlockHtml(b, numbers, ctx));
      const n = Math.min(cells.length, MAX_BLOCKS_PER_ROW);
      const cls = unit.blocks.every(isSignatureStripBlock)
        ? 'cover-parties signature-row block-row'
        : 'cover-parties block-row';
      chunks.push(
        `<div class="${cls}" style="grid-template-columns: repeat(${n}, minmax(0, 1fr))">${cells.join('\n')}</div>`,
      );
      continue;
    }
    chunks.push(renderOneRootBlockHtml(unit.block, numbers, ctx));
  }
  return { bodyHtml: chunks.join('\n'), outline };
}

/* ---------- sheets ---------- */

function signatureReserve(ctx: Ctx): string {
  return `<div class="section" style="margin-top:28px">
    <div class="grid">
      ${sectionAtom('Signature WaPharma', `<div class="sig-pad" aria-hidden="true"></div>`, ctx)}
      ${sectionAtom('Signature partenaire', `<div class="sig-pad" aria-hidden="true"></div>`, ctx)}
    </div>
  </div>`;
}

function docTitleHtml(title: string, kicker?: string | null): string {
  const t = title.trim();
  if (!t) return '';
  const kick = kicker?.trim();
  const kickerHtml = kick ? `<p class="doc-kicker">${escapeHtml(kick)}</p>` : '';
  return `<header class="doc-head">
  <div class="doc-title-row">
    <h1 class="doc-title">${escapeHtml(t)}</h1>
    <div class="doc-title-rule" aria-hidden="true"></div>
  </div>
  ${kickerHtml}
</header>`;
}

function paginationFooter(page: number, pageCount: number): string {
  return `<div class="footer">
    <div class="footerInner">
      <div class="pagination" aria-label="Pagination"><b>${page}</b> / ${pageCount}</div>
    </div>
  </div>`;
}

/** Footer band printed on every page. The PDF step stamps page numbers into it. */
function printFooterChrome(): string {
  return `<div class="footer"><div class="footerInner"></div></div>`;
}

function letterheadChrome(ctx: Ctx): string {
  return `<div class="header">
    <div class="headerInner">
      <img class="brand-logo" src="${ctx.assets.logoDataUri}" alt="WaPharma" />
    </div>
  </div>`;
}

function outlineNavHtml(entries: ConventionOutlineEntry[]): string {
  if (entries.length === 0) return `<p class="empty-hint">Aucune section.</p>`;
  const items = entries
    .map((e) => {
      const num = e.number ? `<span class="doc-outline-num">${escapeHtml(e.number)}</span>` : '';
      const sub = e.level === 2 ? ' doc-outline-item-sub' : '';
      return `<li class="doc-outline-item${sub}">
  ${num}<a href="#${escapeHtml(e.id)}">${escapeHtml(e.title)}</a>
</li>`;
    })
    .join('\n');
  return `<nav class="doc-outline" aria-label="Sommaire">
  <div class="doc-outline-title">Sommaire</div>
  <ol class="doc-outline-list">${items}</ol>
</nav>`;
}

export function assembleConventionDocument(
  input: {
    docTitle: string;
    kicker?: string | null;
    bodyHtml: string;
    outline: ConventionOutlineEntry[];
    opts: ConventionHtmlOptions;
    /** Title/subtitle live in body blocks; content sheet skips its own title. */
    contentUsesBodyTitle?: boolean;
    /** Signatures live in body blocks; skip the fixed signature reserve. */
    omitFixedSignatures?: boolean;
  },
  ctx: Ctx,
): string {
  const sheets: string[] = [];
  const withCover = Boolean(input.opts.coverPage);
  const withToc = Boolean(input.opts.tocPage);
  const pageCount = 1 + (withCover ? 1 : 0) + (withToc ? 1 : 0);
  let page = 1;
  if (withCover) {
    sheets.push(`<div class="sheet sheet-cover">
  <div class="page">
    ${letterheadChrome(ctx)}
    <table class="print-frame" role="presentation"><thead class="print-head-space" aria-hidden="true"><tr><td></td></tr></thead><tbody><tr><td>
    <div class="main cover-main">
      ${docTitleHtml(input.docTitle.trim() || 'Convention', input.kicker)}
    </div>
    </td></tr></tbody><tfoot class="print-foot-space" aria-hidden="true"><tr><td></td></tr></tfoot></table>
    ${paginationFooter(page, pageCount)}
  </div>
</div>`);
    page += 1;
  }
  if (withToc) {
    sheets.push(`<div class="sheet sheet-toc">
  <div class="page toc-page">
    ${letterheadChrome(ctx)}
    <table class="print-frame" role="presentation"><thead class="print-head-space" aria-hidden="true"><tr><td></td></tr></thead><tbody><tr><td>
    <div class="main">
      ${docTitleHtml('Sommaire', null)}
      ${outlineNavHtml(input.outline)}
    </div>
    </td></tr></tbody><tfoot class="print-foot-space" aria-hidden="true"><tr><td></td></tr></tfoot></table>
    ${paginationFooter(page, pageCount)}
  </div>
</div>`);
    page += 1;
  }
  const bodyTitle = input.contentUsesBodyTitle ? '' : input.docTitle;
  const bodyKicker = input.contentUsesBodyTitle ? null : input.kicker;
  const omitSignatures = input.omitFixedSignatures || input.contentUsesBodyTitle;
  sheets.push(`<div class="sheet sheet-body">
  <div class="page">
    ${letterheadChrome(ctx)}
    <table class="print-frame" role="presentation"><thead class="print-head-space" aria-hidden="true"><tr><td></td></tr></thead><tbody><tr><td>
    <div class="main">
      ${docTitleHtml(bodyTitle, bodyKicker)}
      <div class="section compose-body">
        ${input.bodyHtml}
      </div>
      ${omitSignatures ? '' : signatureReserve(ctx)}
    </div>
    </td></tr></tbody><tfoot class="print-foot-space" aria-hidden="true"><tr><td></td></tr></tfoot></table>
    ${paginationFooter(page, pageCount)}
  </div>
</div>`);
  const jobTitle = input.docTitle.trim() || 'Convention WaPharma';
  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapeHtml(jobTitle)}</title>
<style>${conventionDocumentCss(ctx.assets)}</style>
</head>
<body>
  <div class="print-header" aria-hidden="true">${letterheadChrome(ctx)}</div>
  <div class="print-footer" aria-hidden="true">${printFooterChrome()}</div>
  <div class="print-card" aria-hidden="true"></div>
  ${sheets.join('\n')}
</body>
</html>`;
}

/** Title / subtitle from draft blocks. */
export function conventionTitleFromBlocks(blocks: ConventionHtmlBlock[]): {
  title: string;
  subtitle: string;
} {
  const titleBlock = blocks.find((b) => b.kind === 'doc-title');
  const subtitleBlock = blocks.find((b) => b.kind === 'doc-subtitle');
  return {
    title: titleBlock?.title.trim() || titleBlock?.bodyMarkdown.trim() || '',
    subtitle: subtitleBlock?.bodyMarkdown.trim() || subtitleBlock?.title.trim() || '',
  };
}

/** Full A4 HTML document from a block tree. */
export function renderConventionBlocksDocument(
  input: {
    envelopeTitle: string;
    blocks: ConventionHtmlBlock[];
    subtitle?: string | null;
    opts?: ConventionHtmlOptions;
  },
  ctx: Ctx,
): string {
  const opts = input.opts ?? {};
  const fromBlocks = conventionTitleFromBlocks(input.blocks);
  const envelope = fromBlocks.title || input.envelopeTitle.trim();
  const sub = fromBlocks.subtitle || input.subtitle?.trim() || '';
  const title = envelope || sub;
  const kicker = sub && envelope && sub !== envelope ? sub : null;
  if (input.blocks.length === 0) {
    return assembleConventionDocument(
      {
        docTitle: title,
        kicker,
        bodyHtml: `<p class="empty-hint">Ajoutez des sections pour voir l’aperçu.</p>`,
        outline: [],
        opts,
      },
      ctx,
    );
  }
  const { bodyHtml, outline } = renderConventionSectionBlocks(input.blocks, opts, ctx);
  return assembleConventionDocument(
    { docTitle: title, kicker, bodyHtml, outline, opts, contentUsesBodyTitle: true },
    ctx,
  );
}
